// «Популярность и прибыль»: сетка 3×3 — строки буква по прибыли, столбцы по продажам в штуках. Подписи углов
// нейтральные и не совпадают с кнопками рекомендаций (те — по трём буквам). Клетка — фильтр списка блюд (пустая —
// не нажимается). На телефоне — свёрнута в аккордеон.
import React, { useState } from 'react';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ACCORDION_BUTTON_SX, CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../../styles/tokens';
import { LETTERS } from '../../utils/abcCalculations';
import { cellFilter, sameFilter, toggleFilter } from '../../utils/abcList';

const ACCENT = SECTION_ACCENTS['/cost'];
const CORNERS = {
  'A-A': 'Популярные и прибыльные',
  'A-C': 'Прибыльные, но редкие',
  'C-A': 'Популярные, но малоприбыльные',
  'C-C': 'Редкие и малоприбыльные',
};

// Подписи: «Прибыль A» / «Продажи A»; на телефоне строки короче («Приб. A»), чтобы сетка не сжималась
function Grid({ matrix, desktop, filter, onFilter }) {
  const max = Math.max(0, ...matrix.flat());
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `${desktop ? 72 : 52}px repeat(3, minmax(0, 1fr))`, gap: '6px', alignItems: 'stretch' }}>
        <Box />
        {LETTERS.map((qty) => (
          <Typography key={qty} sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, textAlign: 'center' }}>{`Продажи ${qty}`}</Typography>
        ))}
        {LETTERS.map((profit, row) => (
          <React.Fragment key={profit}>
            <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, alignSelf: 'center', whiteSpace: 'nowrap' }}>{desktop ? `Прибыль ${profit}` : `Приб. ${profit}`}</Typography>
            {LETTERS.map((qty, col) => {
              const count = matrix[row][col];
              const corner = CORNERS[`${profit}-${qty}`];
              const strength = max > 0 ? count / max : 0;
              const cell = cellFilter(profit, qty);
              const active = sameFilter(filter, cell);
              return (
                <ButtonBase
                  key={qty}
                  disabled={!count}
                  onClick={() => onFilter(toggleFilter(filter, cell))}
                  aria-pressed={active}
                  aria-label={`Прибыль ${profit}, продажи ${qty}: ${count}${corner ? `, ${corner.toLowerCase()}` : ''}`}
                  sx={{
                    minHeight: { xs: 64, md: 76 },
                    minWidth: 0,
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    boxShadow: active ? '0 0 0 2px #fff' : 'none',
                    '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' },
                    p: 1,
                    borderRadius: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    backgroundColor: count ? `rgba(${ACCENT.rgb},${0.1 + strength * 0.4})` : 'rgba(255,255,255,0.03)',
                  }}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 800, color: count ? '#fff' : 'rgba(255,255,255,0.3)', fontVariantNumeric: 'tabular-nums' }}>
                    {count}
                  </Typography>
                  {corner && (
                    // Узкие клетки на телефоне: перенос по слогам («мало-при-быль-ные»), в крайнем случае — внутри слова
                    <Typography
                      lang="ru"
                      sx={{
                        fontSize: { xs: '10.5px', md: '11px' },
                        lineHeight: 1.25,
                        color: count ? CARD_LABEL_COLOR : 'rgba(255,255,255,0.3)',
                        maxWidth: '100%',
                        hyphens: 'auto',
                        WebkitHyphens: 'auto',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {corner}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Typography sx={{ mt: 1, fontSize: '12px', color: MUTED_TEXT_COLOR }}>
        Строки — группа по прибыли, столбцы — по продажам в штуках
      </Typography>
    </Box>
  );
}

export default function AbcMatrixCard({ matrix, desktop, filter, onFilter }) {
  const [open, setOpen] = useState(false);
  if (desktop) {
    return (
      <Box component="section" aria-label="Популярность и прибыль" sx={{ ...CARD_SURFACE_SX, p: 2.5 }}>
        <Typography component="h3" sx={{ mb: 1.5, fontSize: '15px', fontWeight: 700, color: '#fff' }}>Популярность и прибыль</Typography>
        <Grid matrix={matrix} desktop filter={filter} onFilter={onFilter} />
      </Box>
    );
  }
  return (
    <Box component="section" aria-label="Популярность и прибыль" sx={{ ...CARD_SURFACE_SX, p: 1 }}>
      <ButtonBase onClick={() => setOpen((v) => !v)} aria-expanded={open} sx={ACCORDION_BUTTON_SX}>
        <Typography component="span" sx={{ fontSize: '15px', fontWeight: 700 }}>Популярность и прибыль</Typography>
        <KeyboardArrowDownIcon aria-hidden="true" sx={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ p: 1, pt: 1.5 }}>
          <Grid matrix={matrix} filter={filter} onFilter={onFilter} />
        </Box>
      </Collapse>
    </Box>
  );
}
